import { headerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const bullhorn = defineConnector({
  identifier: "bullhorn",
  name: "Bullhorn",
  tagLine: "Recruiting and staffing software",
  logo,
  protocol: "http",
  tags: ["productivity"],
  defaultUrlPattern: ({ restUrl }) => domainToRegex(restUrl),
  auth: (app) =>
    headerAuth([{ name: "bhresttoken", value: app.oauthToken.accessToken }]),
  steps: [
    {
      title: "Enter your Bullhorn instance details",
      items: [
        {
          kind: "markdown",
          content: "Enter the details for your Bullhorn instance.",
        },
        {
          kind: "url",
          label: "REST URL",
          placeholder: "rest.bullhornstaffing.com/rest-services/...",
          required: true,
          target: { kind: "configuration", field: "restUrl" },
          helperText:
            "Paste the REST URL Bullhorn returns for your account after login.",
        },
        {
          kind: "input",
          label: "Username",
          placeholder: "your-username",
          required: true,
          target: { kind: "configuration", field: "username" },
          helperText: "Your API Username",
        },
        {
          kind: "input",
          label: "Password",
          placeholder: "your-password",
          required: true,
          target: { kind: "configuration", field: "password" },
          helperText: "Your API Password",
        },
      ],
    },
    {
      title: "Create a Bullhorn app",
      items: [
        {
          kind: "markdown",
          content:
            "Open the [Bullhorn REST getting started guide](https://bullhorn.github.io/Getting-Started-with-REST/) and register an API application in your Bullhorn admin settings. Copy the client ID and client secret for the app.",
        },
      ],
    },
    {
      title: "Configure the redirect URI",
      items: [
        {
          kind: "markdown",
          content: "In your app’s settings, add the following redirect URI:",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/bullhorn/callback",
        },
      ],
    },
    {
      title: "Enter your app credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          placeholder: "Paste your Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          placeholder: "Paste your Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Authorize your Bullhorn account",
      items: [
        {
          kind: "markdown",
          content: "Click below to authorize your Bullhorn account.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: () =>
      "https://auth-west.bullhornstaffing.com/oauth/authorize",
    tokenEndpoint: () => "https://auth-west.bullhornstaffing.com/oauth/token",
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: (configuration) => `${configuration.restUrl}/`,
  },
  publicEnvVars: ({ restUrl }) => ({
    BULLHORN_REST_URL: restUrl || "",
  }),
});
